import React, { useMemo, useCallback, useState } from "react";
import { NavLink, Button } from "../../components";
import { makeStyles } from "@material-ui/styles";
import Header from "./Header";
import sharedStyles from "../styles";
import { Helmet } from "react-helmet";
import { useTaskDefs } from "../../data/task";
import AgGridClient from "../../components/AgGridClient";
import {
  useCellRenderer,
  tooltipGetters,
} from "../../utils/cellRendererFactory";

const useStyles = makeStyles(sharedStyles);

export default function TaskDefinitions(props) {
  const permissions = props?.permissions;
  const classes = useStyles();
  const { data: tasks, isFetching, refetch } = useTaskDefs();

  const [gridApi, setGridApi] = useState(null);

  const handleGridReady = useCallback((params) => {
    setGridApi(params.api);
  }, []);

  const simpleRenderer = useCellRenderer("simple");
  const timeRenderer = useCellRenderer("time");
  const taskDefLinkRenderer = useCellRenderer("taskDefLink");
  const jsonArrayRenderer = useCellRenderer("jsonArray");
  const jsonFullRenderer = useCellRenderer("jsonFull");

  const columnDefs = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        cellRenderer: taskDefLinkRenderer,
        filter: "agTextColumnFilter",
        // tooltipValueGetter: tooltipGetters.taskDefLink,
        // headerTooltip: "Task Name",
        width: 250,
        minWidth: 200,
        flex: 1,
        sort: "asc",
      },
      {
        field: "description",
        headerName: "Description",
        cellRenderer: simpleRenderer,
        filter: "agTextColumnFilter",
        // tooltipValueGetter: tooltipGetters.simple,
        // headerTooltip: "Task Description",
        width: 250,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "createTime",
        headerName: "Create Time",
        filter: "agDateColumnFilter",
        cellRenderer: timeRenderer,
        // tooltipValueGetter: tooltipGetters.time,
        // headerTooltip: "Creation Time",
        width: 250,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "ownerEmail",
        headerName: "Owner Email",
        cellRenderer: simpleRenderer,
        filter: "agTextColumnFilter",
        // tooltipValueGetter: tooltipGetters.simple,
        // headerTooltip: "Owner Email Address",
        width: 250,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "inputKeys",
        headerName: "Input Keys",
        filter: "agTextColumnFilter",
        cellRenderer: jsonArrayRenderer,
        // tooltipValueGetter: tooltipGetters.jsonArray,
        // headerTooltip: "Input Keys",
        width: 250,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "outputKeys",
        headerName: "Output Keys",
        filter: "agTextColumnFilter",
        cellRenderer: jsonArrayRenderer,
        // tooltipValueGetter: tooltipGetters.jsonArray,
        // headerTooltip: "Output Keys",
        width: 250,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "timeoutPolicy",
        headerName: "Timeout Policy",
        cellRenderer: simpleRenderer,
        filter: "agTextColumnFilter",
        // tooltipValueGetter: tooltipGetters.simple,
        // headerTooltip: "Timeout Policy",
        width: 250,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "timeoutSeconds",
        headerName: "Timeout Seconds",
        cellRenderer: simpleRenderer,
        filter: "agNumberColumnFilter",
        // tooltipValueGetter: tooltipGetters.simple,
        // headerTooltip: "Timeout (Seconds)",
        width: 250,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "retryCount",
        headerName: "Retry Count",
        cellRenderer: simpleRenderer,
        filter: "agNumberColumnFilter",
        // tooltipValueGetter: tooltipGetters.simple,
        // headerTooltip: "Retry Count",
        width: 250,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "retryLogic",
        headerName: "Retry Logic",
        cellRenderer: simpleRenderer,
        filter: "agTextColumnFilter",
        // tooltipValueGetter: tooltipGetters.simple,
        // headerTooltip: "Retry Logic",
        width: 250,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "retryDelaySeconds",
        headerName: "Retry Delay Seconds",
        cellRenderer: simpleRenderer,
        filter: "agNumberColumnFilter",
        // tooltipValueGetter: tooltipGetters.simple,
        // headerTooltip: "Retry Delay (Seconds)",
        width: 250,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "responseTimeoutSeconds",
        headerName: "Response Timeout Seconds",
        cellRenderer: simpleRenderer,
        filter: "agNumberColumnFilter",
        // tooltipValueGetter: tooltipGetters.simple,
        // headerTooltip: "Response Timeout (Seconds)",
        width: 250,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "inputTemplate",
        headerName: "Input Template",
        filter: "agTextColumnFilter",
        cellRenderer: jsonFullRenderer,
        // tooltipValueGetter: tooltipGetters.jsonFull,
        // headerTooltip: "Input Template",
        width: 250,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "rateLimitPerFrequency",
        headerName: "Rate Limit Per Frequency",
        cellRenderer: simpleRenderer,
        filter: "agNumberColumnFilter",
        // tooltipValueGetter: tooltipGetters.simple,
        // headerTooltip: "Rate Limit Per Frequency",
        width: 250,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "rateLimitFrequencyInSeconds",
        headerName: "Rate Limit Frequency In Seconds",
        cellRenderer: simpleRenderer,
        filter: "agNumberColumnFilter",
        // tooltipValueGetter: tooltipGetters.simple,
        // headerTooltip: "Rate Limit Frequency (Seconds)",
        width: 250,
        minWidth: 200,
        flex: 1,
      },

      {
        field: "concurrentExecLimit",
        headerName: "Concurrent Exec Limit",
        cellRenderer: simpleRenderer,
        filter: "agNumberColumnFilter",
        // tooltipValueGetter: tooltipGetters.simple,
        // headerTooltip: "Concurrent Execution Limit",
        width: 250,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "pollTimeoutSeconds",
        headerName: "Poll Timeout Seconds",
        cellRenderer: simpleRenderer,
        filter: "agNumberColumnFilter",
        // tooltipValueGetter: tooltipGetters.simple,
        // headerTooltip: "Poll Timeout (Seconds)",
        width: 250,
        minWidth: 200,
        flex: 1,
      },
    ],
    [
      simpleRenderer,
      timeRenderer,
      taskDefLinkRenderer,
      jsonArrayRenderer,
      jsonFullRenderer,
    ]
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
    }),
    []
  );

  const defaultVisibleColumns = useMemo(
    () => ["name", "description", "ownerEmail"],
    []
  );

  const [visibleColumns, setVisibleColumns] = useState(defaultVisibleColumns);

  const handleColumnSelectionChange = useCallback((selected) => {
    setVisibleColumns(selected);
  }, []);

  // Filter columnDefs based on visibleColumns
  const filteredColumnDefs = useMemo(
    () => columnDefs.filter((col) => visibleColumns.includes(col.field)),
    [columnDefs, visibleColumns]
  );

  const actionButton = (
    <div>
      {permissions?.tasks?.actions?.create_task === "True" && (
        <Button
          variant="contained"
          color="primary"
          component={NavLink}
          path="/taskDef"
          style={{
            margin: "0.5rem 0.3rem",
            padding: "0.5rem",
            color: "rgb(255,255,255)",
            height: "32px",
          }}
        >
          Define task
        </Button>
      )}
    </div>
  );

  return (
    <div
      className={classes.wrapper}
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
    >
      <Helmet>
        <title>Conductor UI - Task Definitions</title>
      </Helmet>

      <Header loading={isFetching} permissions={permissions} />

      <div
        className={classes.tabContent}
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          paddingTop: "0px",
          paddingBottom: "60px",
        }}
      >
        {tasks && (
          <AgGridClient
            rowData={tasks}
            columnDefs={filteredColumnDefs}
            defaultColDef={defaultColDef}
            paginationPageSize={5}
            onGridReady={handleGridReady}
            gridApi={gridApi}
            selectedColumns={visibleColumns}
            defaultVisibleColumns={defaultVisibleColumns}
            onColumnSelectionChange={handleColumnSelectionChange}
            allColumnDefs={columnDefs}
            actionButton={actionButton}
          />
        )}
      </div>
    </div>
  );
}
